import { exactCount, exactInteger, exactPositive } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, TableBlock, Tone } from '@/kova/spec/types'

import type { ApiClient } from '@/lib/api'

import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { attestForm, grantRoleForm, revokeRoleForm } from './accesscontrols'
import type { Caller } from './handles'
import { callerBeside } from './writes'

/**
 * Access & Audit › Staff directory (`ac-staff`) and Access reviews
 * (`ac-reviews`), both read from `GET /v1/admin/staff` (go-wallet#235, handler
 * `internal/server/admin_access.go`).
 *
 * # Two screens, one read, and that is deliberate
 *
 * The endpoint answers both questions in one row: who holds what, and when that
 * was last confirmed. Splitting it into two reads would let the directory and
 * the attestation view disagree about the same person — one of them refreshed,
 * the other not — on the pair of screens whose whole purpose is that somebody's
 * access is what a human last agreed it should be.
 *
 * The screens differ in what they put in front: the directory leads with roles
 * and offboarding, the review view leads with the attestation and its deadline.
 * They render the same underlying rows and cannot contradict each other.
 *
 * # The finding is the difference between two role sets
 *
 * `roles` is what is granted TODAY. `attested_roles` is what a reviewer
 * confirmed at the last attestation. The endpoint documents the gap as the point
 * of the whole thing:
 *
 *   > a contractor who was attested as Support in January and holds Compliance
 *   > now.
 *
 * So a row whose two sets differ is called out, in its own tone, rather than
 * being left for somebody to spot by reading two columns side by side.
 *
 * # Overdue does not revoke, and the screen must not imply it did
 *
 * `NOTHING IS REVOKED ON OVERDUE — the state stays loud until a human acts,
 * which is why roles still lists what an overdue person holds.` The row keeps
 * its roles, keeps them legible, and carries the overdue count in days beside
 * them. A console that greyed out an overdue person's roles would be showing an
 * access removal that did not happen.
 *
 * # Never attested is not overdue
 *
 * Two separate counters, and the endpoint keeps them separate for a reason an
 * operator acts on: overdue is a lapse in the cadence, never-attested is
 * usually an onboarding that skipped a step. They are different conversations
 * with different people.
 *
 * # No credential, ever
 *
 * Part I §6 and law 11. `subject` is the identity provider's opaque id — an
 * external reference we join on. There is no password, hash, TOTP seed,
 * recovery code or session token in this payload; the backend pins its
 * serialised field set with a test so adding one has to be deliberate, and
 * nothing here would render it if it arrived.
 *
 * MFA enrolment appears in the corpus design and is NOT in this payload. It is
 * therefore absent from the table rather than drawn empty or, worse, filled in
 * with a green "enrolled" that nobody read — see `MISSING`.
 *
 * # Read-only
 *
 * `POST /staff`, `POST /staff/{subject}/roles`, the revoke route and
 * `POST /staff/{subject}/access-reviews` all exist and none is called here. A
 * grant is a change that has to argue for itself in its own issue, with the
 * maker-checker and reason-code surface law 21 requires — not one inherited
 * from the change that made the directory readable.
 */

// ---------------------------------------------------------------------------
// The wire shape — mirrors internal/server/admin_access.go json tags
// ---------------------------------------------------------------------------

/** `current`, `overdue` or `never_attested`, computed against one instant. */
export type Attestation = 'current' | 'overdue' | 'never_attested'

export interface StaffRowView {
  readonly subject: string
  readonly staff_id: number
  readonly display_name?: string
  readonly roles?: readonly unknown[]
  readonly disabled?: boolean
  readonly disabled_at?: string
  readonly attestation?: string
  readonly attested_at?: string
  readonly attested_by?: string
  readonly next_due_at?: string
  readonly attested_roles?: readonly unknown[]
  readonly overdue_days?: number
}

export interface StaffResponse {
  readonly staff: readonly unknown[]
  readonly overdue?: unknown
  readonly never_attested?: unknown
  readonly review_interval_days?: unknown
  readonly as_of?: unknown
}

export const STAFF_PATH = '/staff'

const SOURCE = 'postgres · staff_users + staff_role_grants + staff_access_reviews (GET /v1/admin/staff)'
const AUTHORITY = 'operational · our directory, never the identity provider'

/**
 * What this read does NOT answer for, stated on every screen it draws.
 *
 * The design's directory carries an MFA column and a last-active column. Both
 * are facts about the identity provider's session handling, not about our
 * directory, and neither is in this payload. Drawing them empty would read as
 * "not enrolled" and "never active" — two claims nobody made, on the screen an
 * access review is conducted from.
 */
const MISSING: readonly string[] = [
  'MFA enrolment — held by the identity provider and not reported here',
  'last active — this console never asks the identity provider directly (Part I §6)',
]

// ---------------------------------------------------------------------------
// Reading a row
// ---------------------------------------------------------------------------

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function roleList(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return []
  return value.filter((r): r is string => typeof r === 'string' && r !== '')
}

interface StaffRow {
  readonly subject: string
  readonly staffId: number | null
  readonly displayName: string
  readonly roles: readonly string[]
  readonly disabled: boolean | null
  readonly disabledAt: string
  /** `null` when the backend sent a value this console does not know. */
  readonly attestation: Attestation | null
  readonly attestedAt: string
  readonly attestedBy: string
  readonly nextDueAt: string
  readonly attestedRoles: readonly string[]
  readonly overdueDays: number | null
}

const ATTESTATIONS: readonly string[] = ['current', 'overdue', 'never_attested']

function readStaff(raw: unknown): StaffRow | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const s = raw as Partial<StaffRowView>
  const subject = text(s.subject)
  // A row with no subject identifies nobody. It is refused rather than rendered
  // as a person with a blank id, which on this screen would be a directory
  // entry an auditor cannot resolve to a human.
  if (subject === '') return null
  const attestation = text(s.attestation)
  return {
    subject,
    // A staff id is a reference an operator quotes. `exactPositive` (#207):
    // zero is not a staff row, and an id past exact integer range names a
    // different person while looking entirely ordinary.
    staffId: exactPositive(s.staff_id),
    displayName: text(s.display_name),
    roles: roleList(s.roles),
    // Three states. `false` and "the backend did not say" are different, and
    // defaulting the second to `false` would render an offboarded person as
    // active on the screen offboarding is checked from.
    disabled: typeof s.disabled === 'boolean' ? s.disabled : null,
    disabledAt: text(s.disabled_at),
    attestation: ATTESTATIONS.includes(attestation) ? (attestation as Attestation) : null,
    attestedAt: text(s.attested_at),
    attestedBy: text(s.attested_by),
    nextDueAt: text(s.next_due_at),
    attestedRoles: roleList(s.attested_roles),
    overdueDays: exactInteger(s.overdue_days),
  }
}

/** A date-time as a date. The attestation cadence is in days, not minutes. */
function day(at: string): string {
  return at === '' ? '' : at.slice(0, 10)
}

/**
 * The identity cell: the name an admin typed, and the subject beneath it.
 *
 * An empty name stays empty and says so. The backend leaves it empty on
 * purpose, and a directory that printed the opaque subject in the name column
 * would make every unnamed row look identical to every other at a glance —
 * on the screen where "who is this person" is the question being asked.
 */
function identityCell(s: StaffRow): Cell {
  return [s.displayName === '' ? '— no name recorded' : s.displayName, s.subject]
}

/**
 * The attestation chip.
 *
 * `overdue` carries its day count because "how long" is what decides whether
 * this is a reminder or an escalation, and the endpoint sends it precisely so
 * the console does not compute a deadline against a browser clock.
 */
function attestationChip(s: StaffRow): ChipCell {
  if (s.attestation === null) return ['— not stated', 'r']
  if (s.attestation === 'current') return ['current', 'g']
  if (s.attestation === 'never_attested') return ['never attested', 'r']
  const days = s.overdueDays === null ? '' : ` ${String(s.overdueDays)}d`
  return [`OVERDUE${days}`, 'r']
}

/** Whether the roles held now differ from the roles a reviewer confirmed. */
function drifted(s: StaffRow): boolean {
  if (s.attestation === null || s.attestation === 'never_attested') return false
  if (s.attestedRoles.length === 0) return false
  const held = new Set(s.roles.map((r) => r.toLowerCase()))
  const attested = new Set(s.attestedRoles.map((r) => r.toLowerCase()))
  if (held.size !== attested.size) return true
  for (const r of held) if (!attested.has(r)) return true
  return false
}

/**
 * The roles cell.
 *
 * An empty grant set is a REAL state — a live directory row with everything
 * revoked — and the endpoint says so. It reads as such rather than as a blank,
 * because a blank in this column is indistinguishable from a column that failed
 * to render, and the difference is whether somebody still has access.
 */
function rolesText(roles: readonly string[]): string {
  return roles.length === 0 ? '— none granted' : roles.join(', ')
}

function standingChip(s: StaffRow): ChipCell {
  if (s.disabled === null) return ['— not stated', 'r']
  if (s.disabled) {
    const when = day(s.disabledAt)
    return [when === '' ? 'offboarded' : `offboarded ${when}`, 'n']
  }
  return ['active', 'g']
}

// ---------------------------------------------------------------------------
// ac-staff — the directory
// ---------------------------------------------------------------------------

const DIRECTORY_COLS: readonly Column[] = [
  ['Identity', 's', 'minmax(200px,1.4fr)'],
  ['Staff id', 'm', '90px'],
  ['Roles held', 'x', 'minmax(190px,1.3fr)'],
  ['Standing', 'c', '150px'],
  ['Access review', 'c', '150px'],
  ['Confirmed as', 'x', 'minmax(170px,1.2fr)'],
]

function directoryRow(s: StaffRow): readonly Cell[] {
  return [
    identityCell(s),
    s.staffId === null ? '— not returned' : String(s.staffId),
    rolesText(s.roles),
    standingChip(s),
    attestationChip(s),
    // The comparison the endpoint exists to make legible. Same-as is stated in
    // words rather than left blank: a blank here would read as "nothing to
    // report" on a row nobody has ever reviewed.
    s.attestation === 'never_attested'
      ? '— never confirmed'
      : s.attestedRoles.length === 0
        ? '— not returned'
        : drifted(s)
          ? `${s.attestedRoles.join(', ')} — differs from what is held`
          : `${s.attestedRoles.join(', ')} — unchanged`,
  ]
}

function directoryTable(rows: readonly StaffRow[], unreadable: number): TableBlock {
  const drift = rows.filter(drifted).length
  const head = `${String(rows.length)} staff row(s)`
  const withDrift = drift === 0 ? head : `${head} · ${String(drift)} holding roles a review did not confirm`
  return {
    t: 'table',
    title: 'staff_users · granted roles and review standing',
    src: 'ledger',
    facets: false,
    cols: DIRECTORY_COLS,
    rows: rows.map(directoryRow),
    footer: unreadable === 0 ? withDrift : `${withDrift} · ${String(unreadable)} row(s) could not be read`,
    foot2:
      'No password, hash, TOTP seed, recovery code or session token appears here or in the payload behind it — the subject is the identity provider’s opaque reference and nothing else. Roles are what was GRANTED, unexpanded: what a role implies is a different question and the backend answers it on every request.',
  }
}

const ROLE_SOURCE_NOTE: Block = {
  t: 'note',
  tone: 'n',
  kicker: 'Resolved',
  title: 'Roles are ours, and the design’s open question is closed',
  body: 'The corpus carries an amber note asking whether staff roles live in identity-provider traits or in an operator-owned table (brief §14.2). They live in ours: staff_users and staff_role_grants, joined to the provider’s identity by subject. That is what makes a role change auditable in our own log, revocable without a round trip to a third party, and reviewable on this screen. The identity provider authenticates and never authorizes.',
  ref: 'CLAUDE.md Part I §6 · grants are append-only and immutable after insert',
}

/**
 * What a role grant actually does, said before the controls.
 *
 * Roles NEST — a compliance officer reaches everything ops and support hold —
 * so granting one grants more than its name suggests, and revoking one may
 * leave somebody with the same reach through another. An admin who reads the
 * role list as a set of independent switches will grant too much and revoke
 * too little, and neither mistake announces itself.
 */
const ROLES_NEST: Block = {
  t: 'note',
  tone: 'a',
  kicker: 'Before you grant anything',
  title: 'Roles nest, so a grant reaches further than its name',
  body: 'Support, ops and compliance nest in that order: a compliance officer already holds everything an ops analyst does. Granting compliance therefore grants ops and support with it, and revoking ops from somebody who also holds compliance changes nothing they can reach. Admin is the exception and nests with nothing — an admin holds the directory and break-glass, and holds no compliance authority unless it was granted separately. The confirmation on every grant and revocation shows the whole resulting set for this reason, not just the role that was named.',
  ref: 'support ⊂ ops ⊂ compliance · admin implies nothing',
}

// ---------------------------------------------------------------------------
// ac-reviews — the same rows, led by the attestation
// ---------------------------------------------------------------------------

const REVIEW_COLS: readonly Column[] = [
  ['Staff', 's', 'minmax(200px,1.4fr)'],
  ['Roles held', 'x', 'minmax(180px,1.3fr)'],
  ['Last attested', 'm', '132px'],
  ['Attested by', 'x', '150px'],
  ['Due', 'm', '132px'],
  ['State', 'c', '150px'],
]

function reviewRow(s: StaffRow): readonly Cell[] {
  return [
    identityCell(s),
    rolesText(s.roles),
    day(s.attestedAt) === '' ? '— never' : day(s.attestedAt),
    s.attestedBy === '' ? '— unattributed' : s.attestedBy,
    day(s.nextDueAt) === '' ? '— not set' : day(s.nextDueAt),
    attestationChip(s),
  ]
}

/**
 * Overdue first, then never-attested, then the rest.
 *
 * The one sort in either screen, and it is a presentation order over a set the
 * backend returned whole — not a filter. Nothing is hidden by it; the rows that
 * need a human are simply at the top, which is the difference between a screen
 * an auditor reads and a screen an auditor scrolls.
 */
function reviewOrder(rows: readonly StaffRow[]): readonly StaffRow[] {
  const rank = (s: StaffRow): number => {
    if (s.attestation === null) return 0
    if (s.attestation === 'overdue') return 1
    if (s.attestation === 'never_attested') return 2
    return 3
  }
  return [...rows].sort((a, b) => rank(a) - rank(b))
}

function reviewTable(rows: readonly StaffRow[], unreadable: number): TableBlock {
  const overdue = rows.filter((s) => s.attestation === 'overdue').length
  const never = rows.filter((s) => s.attestation === 'never_attested').length
  const head = `${String(rows.length)} staff · ${String(overdue)} overdue · ${String(never)} never attested`
  return {
    t: 'table',
    title: 'Attestation',
    src: 'ledger',
    facets: false,
    cols: REVIEW_COLS,
    rows: reviewOrder(rows).map(reviewRow),
    footer: unreadable === 0 ? head : `${head} · ${String(unreadable)} row(s) could not be read`,
    foot2:
      'An overdue attestation does not auto-revoke — it stays loud until a human acts, which is why an overdue row still lists every role it holds. Overdue and never-attested are counted separately because they are different conversations with different people.',
  }
}

function driftNote(rows: readonly StaffRow[]): Block | null {
  const drifted_ = rows.filter(drifted)
  if (drifted_.length === 0) return null
  const who = drifted_
    .map((s) => (s.displayName === '' ? s.subject : s.displayName))
    .join(', ')
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Finding',
    title: `${String(drifted_.length)} person(s) hold roles the last review did not confirm`,
    body: 'The attestation is current and the role set has changed since it was taken. That is the finding this comparison exists for — a contractor attested as Support who now holds Compliance is inside their review window and outside what anybody agreed to. It is stated rather than left for somebody to notice by reading two columns against each other.',
    ref: who,
  }
}

// ---------------------------------------------------------------------------
// Shared composition
// ---------------------------------------------------------------------------

interface Parsed {
  readonly rows: readonly StaffRow[]
  readonly unreadable: number
  readonly overdue: number | null
  readonly neverAttested: number | null
  readonly intervalDays: number | null
  readonly asOf: string
}

function count(value: unknown): number | null {
  return exactCount(value)
}

function parse(res: StaffResponse): Parsed {
  const rows: StaffRow[] = []
  let unreadable = 0
  for (const raw of res.staff) {
    const parsed = readStaff(raw)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }
  return {
    rows,
    unreadable,
    overdue: count(res.overdue),
    neverAttested: count(res.never_attested),
    intervalDays: count(res.review_interval_days),
    asOf: text(res.as_of),
  }
}

/**
 * Whether the reported counts are contradicted by the page beneath them.
 *
 * The same posture the AML queue takes to a depth its own page disagrees with:
 * two statements from the backend that cannot both be true are reported as a
 * disagreement, never resolved in favour of the calmer one. Here the calmer one
 * is `overdue: 0` over a page containing overdue rows, and an access review
 * conducted from that reads "nothing to do".
 */
function contradiction(p: Parsed): Block | null {
  const overdueRows = p.rows.filter((s) => s.attestation === 'overdue').length
  const neverRows = p.rows.filter((s) => s.attestation === 'never_attested').length
  const clashes: string[] = []
  if (p.overdue !== null && p.overdue !== overdueRows) {
    clashes.push(`overdue: reported ${String(p.overdue)}, ${String(overdueRows)} on this page`)
  }
  if (p.neverAttested !== null && p.neverAttested !== neverRows) {
    clashes.push(
      `never attested: reported ${String(p.neverAttested)}, ${String(neverRows)} on this page`,
    )
  }
  if (clashes.length === 0) return null
  return {
    t: 'note',
    tone: 'a',
    kicker: 'Disagreement',
    title: 'The counts and the rows beneath them do not agree',
    body: 'Both figures came from this one read, so neither is stale in the ordinary sense, and neither is stated here as the answer. Attest or offboard somebody between the count and the page and this is what it looks like; so is a filter nobody meant to apply. Either way the number to act on is the one you can see rows for.',
    ref: clashes.join(' · '),
  }
}

function provenanceOf(p: Parsed, readAt: Date): DataSource {
  const missing = [...MISSING]
  if (p.overdue === null) missing.push('the overdue count')
  if (p.neverAttested === null) missing.push('the never-attested count')
  return {
    kind: 'partial',
    source: SOURCE,
    authority: AUTHORITY,
    // The backend stamps the instant it computed every row's attestation
    // against; that is a better answer than the browser's clock and is used
    // when it arrives.
    readAt: p.asOf === '' ? readAtStamp(readAt) : `${p.asOf.slice(0, 10)} ${p.asOf.slice(11, 16)} UTC`,
    missing,
  }
}

function unreadableRowsNote(count_: number, tone: Tone = 'a'): Block {
  return {
    t: 'note',
    tone,
    kicker: 'Partial read',
    title: `${String(count_)} directory row(s) could not be read`,
    body: 'Counted rather than dropped silently. A directory rendered shorter than the record, with nothing saying so, would let an access review sign off on a set of people that is not the set that exists.',
    ref: 'a row with no subject identifies nobody and is refused rather than drawn blank',
  }
}

function emptyNote(): Block {
  return {
    t: 'note',
    tone: 'r',
    kicker: 'Staff directory',
    title: 'The directory came back empty',
    body: 'Nobody at all holds a staff row. That is not a normal state for a system somebody is signed in to — a session reaching this screen resolved through the directory, so at least one row exists for the person reading it. Treat an empty directory as a read that went somewhere unexpected rather than as an empty organisation.',
    ref: 'staff_users · the read returned zero rows',
  }
}

/** The client and the caller, or `null` on a surface that only reads. */
export interface Writer {
  readonly api: ApiClient
  readonly caller: Caller
}

export function composeStaffDirectory(
  res: StaffResponse, readAt: Date, writer: Writer | null = null,
): ScreenData {
  const p = parse(res)
  const blocks: Block[] = [directoryTable(p.rows, p.unreadable)]
  if (p.unreadable > 0) blocks.push(unreadableRowsNote(p.unreadable))
  if (p.rows.length === 0 && p.unreadable === 0) blocks.push(emptyNote())
  const clash = contradiction(p)
  if (clash !== null) blocks.push(clash)
  blocks.push(ROLE_SOURCE_NOTE, ROLES_NEST)
  if (writer !== null) {
    blocks.push(
      grantRoleForm(writer.api, writer.caller),
      revokeRoleForm(writer.api, writer.caller),
    )
  }
  return {
    blocks,
    provenance: provenanceOf(p, readAt),
    rowKeys: p.rows.map((s) => s.subject),
  }
}

export function composeAccessReviews(
  res: StaffResponse, readAt: Date, writer: Writer | null = null,
): ScreenData {
  const p = parse(res)
  const blocks: Block[] = [reviewTable(p.rows, p.unreadable)]
  if (p.unreadable > 0) blocks.push(unreadableRowsNote(p.unreadable))
  if (p.rows.length === 0 && p.unreadable === 0) blocks.push(emptyNote())
  const drift = driftNote(p.rows)
  if (drift !== null) blocks.push(drift)
  const clash = contradiction(p)
  if (clash !== null) blocks.push(clash)
  blocks.push({
    t: 'note',
    tone: 'n',
    quiet: true,
      kicker: 'What an attestation is',
    title: 'Attesting records what was found. It changes nobody’s access',
    body: 'There is no revoke on this screen and there is no route for one: overdue is a state this surface reports and never an action it takes. A reviewer who finds something wrong records changes_requested, and somebody with the role-write permission then makes the change as a separate act with its own audit row — possibly the same person, a moment later, on the staff directory. Folding the two together would make an attestation and a revocation indistinguishable in the trail, which is the one thing an access review exists to keep apart.',
    ref: 'the review store has no revoke method · the absence is the assertion',
  })
  if (writer !== null) blocks.push(attestForm(writer.api, writer.caller))
  return {
    blocks,
    provenance: provenanceOf(p, readAt),
    rowKeys: reviewOrder(p.rows).map((s) => s.subject),
  }
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

function unreadableScreen(what: string, reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: what,
      title: 'The staff directory could not be read',
      body: `${reason} No row, no role and no attestation below is known — which is different from nobody holding a role, and different again from every attestation being current.`,
      ref: decisionRef === null
        ? 'The directory is Admin’s. A refusal here is expected for every other role.'
        : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function isStaffResponse(payload: unknown): payload is StaffResponse {
  if (typeof payload !== 'object' || payload === null) return false
  return Array.isArray((payload as Partial<StaffResponse>).staff)
}

async function readDirectory(
  api: Parameters<ScreenLoader>[0],
  what: string,
  compose: (res: StaffResponse, readAt: Date, writer: Writer | null) => ScreenData,
): Promise<ScreenData> {
  const calling = callerBeside(api)
  let payload: unknown
  try {
    payload = await api.get<unknown>(STAFF_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        what,
        `The staff endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen(what, 'The backend could not be reached.', null)
  }

  if (!isStaffResponse(payload)) {
    return unreadableScreen(
      what,
      'The staff endpoint returned a shape this console does not know.',
      null,
    )
  }
  return compose(payload, new Date(), { api, caller: await calling })
}

export const loadStaffDirectory: ScreenLoader = (api) =>
  readDirectory(api, 'Staff directory', composeStaffDirectory)

export const loadAccessReviews: ScreenLoader = (api) =>
  readDirectory(api, 'Access reviews', composeAccessReviews)

// ---------------------------------------------------------------------------
// One person's attestation history — GET /v1/admin/staff/{subject}/access-reviews
// ---------------------------------------------------------------------------

/**
 * The history behind a directory row.
 *
 * The directory says WHEN somebody was last confirmed. This says what was
 * confirmed each time, and `roles_attested` is the role set AS SNAPSHOTTED —
 * "which is not necessarily what the person holds now, and the difference
 * between the two is the finding".
 *
 * `changes_requested` records a finding and changes nothing. Roles are
 * withdrawn by the revoke endpoint, "never by a verdict or a date", so a review
 * with that outcome must not render as a revocation — it renders as a finding
 * somebody still has to act on.
 *
 * Hangs off a row rather than a screen, so it is not in `SCREEN_LOADERS`. See
 * `openers.ts`.
 */
export function accessReviewsPath(subject: string): string {
  return `${STAFF_PATH}/${encodeURIComponent(subject)}/access-reviews`
}

const REVIEWS_SOURCE = 'postgres · staff_access_reviews (GET /v1/admin/staff/{subject}/access-reviews)'

const REVIEW_HISTORY_COLS: readonly Column[] = [
  ['Attested', 'm', '130px'],
  ['By', 'x', 'minmax(150px,1.1fr)'],
  ['Outcome', 'c', '180px'],
  ['Roles confirmed then', 'x', 'minmax(190px,1.3fr)'],
  ['Next due', 'm', '130px'],
]

function reviewHistoryRow(raw: unknown): readonly Cell[] | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const r = raw as Record<string, unknown>
  const outcome = text(r['outcome'])
  const roles = roleList(r['roles_attested'])
  return [
    day(text(r['attested_at'])) === '' ? '— not returned' : day(text(r['attested_at'])),
    text(r['attested_by']) === '' ? '— unattributed' : text(r['attested_by']),
    outcome === 'confirmed'
      ? (['confirmed', 'g'] as ChipCell)
      : outcome === 'changes_requested'
        ? (['CHANGES REQUESTED', 'a'] as ChipCell)
        : ([outcome === '' ? '— not stated' : `unknown: ${outcome}`, 'r'] as ChipCell),
    roles.length === 0 ? '— none recorded' : roles.join(', '),
    day(text(r['next_due_at'])) === '' ? '— not set' : day(text(r['next_due_at'])),
  ]
}

export async function readAccessReviews(api: ApiClient, subject: string): Promise<ScreenData> {
  const wanted = subject.trim()
  if (wanted === '') {
    const reason = 'No subject was given, so no history was looked up.'
    return {
      blocks: [{
        t: 'note', tone: 'r', kicker: 'Access reviews',
        title: 'No attestation history was requested',
        body: reason,
        ref: 'a directory row with no subject identifies nobody',
      }],
      provenance: { kind: 'unread', source: REVIEWS_SOURCE, reason },
    }
  }

  let payload: unknown
  try {
    payload = await api.get<unknown>(accessReviewsPath(wanted))
  } catch (error) {
    const reason = error instanceof ApiError
      ? `The history endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`
      : 'The backend could not be reached.'
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return {
      blocks: [{
        t: 'note', tone: 'r', kicker: 'Access reviews',
        title: `The attestation history for ${wanted} could not be read`,
        body: `${reason} Whether this person has ever been reviewed, and what was confirmed when they were, is unknown from this read — which is different from never having been attested.`,
        ref: error instanceof ApiError && error.decisionRef !== null
          ? `decision_ref ${error.decisionRef}`
          : `subject asked for: ${wanted}`,
      }],
      provenance: { kind: 'unread', source: REVIEWS_SOURCE, reason },
    }
  }

  const list = typeof payload === 'object' && payload !== null && !Array.isArray(payload)
    ? (payload as Record<string, unknown>)['reviews']
    : undefined
  const raw = Array.isArray(list) ? (list as readonly unknown[]) : []
  const rows: (readonly Cell[])[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = reviewHistoryRow(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const blocks: Block[] = [{
    t: 'table',
    title: `Attestation history — ${wanted}`,
    src: 'ledger',
    facets: false,
    cols: REVIEW_HISTORY_COLS,
    rows,
    footer: `${String(rows.length)} review(s)${unreadable === 0 ? '' : ` · ${String(unreadable)} could not be read`}`,
    foot2:
      'The roles column is the set as SNAPSHOTTED at that review, which is not necessarily what this person holds now — and the difference between the two is the finding. `changes_requested` records a finding and changes nothing: roles are withdrawn by an explicit revocation, never by a verdict or a date.',
  }]

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Never reviewed',
      title: 'This person has never been attested',
      body: 'Which is a different finding from an overdue attestation — usually an onboarding that skipped a step rather than a lapse in the cadence, and a different conversation with a different person.',
      ref: `${wanted} · no review on file`,
    })
  }

  return {
    blocks,
    provenance: {
      kind: 'live',
      source: REVIEWS_SOURCE,
      authority: AUTHORITY,
      readAt: readAtStamp(new Date()),
    },
  }
}
